/**
 * TermsConsentSection — 회원가입 약관 동의 공통 컴포넌트
 *
 * 4개 가입 페이지(MasterSignupPage/StaffSignupPage/MasterSnsSignupPage/StaffSnsSignupPage)에서 공용으로 쓴다.
 * 기획서(docs/input/indipia_이용약관및운영정책 방식 기획(화면구성예시).html)의 가입 동의 카드를 그대로 포팅:
 *   - 필수 1개: 이용약관 동의(개인정보 수집·이용 안내 포함, 별도 체크박스 없음)
 *   - 선택 1개: 마케팅 정보 수신 동의 — 체크 시 채널(이메일/SMS·알림톡) 기본 선택 + 야간(21시~08시) 광고 수신 옵션 노출
 *     (앱 푸시는 네이티브 앱이 있는 InsolMobile 전용 — 웹은 제공하지 않음)
 * 위치정보·데이터수집 동의는 가입 화면에서 제외 — 가입 후 설정 화면(수신동의)에서 개별로 받는다.
 *
 * 동의 행은 ui-core `Checkbox` 의 `bare` 변형이다 — 행 전체가 이미 `<label>` 이라 label 중첩을 피한다
 * (D-09 §15.2). 이전 구현은 장식용 `<div>` 라 키보드 포커스·aria-checked 가 없었다.
 */
import { useState } from 'react';
import { Button, Checkbox, Radio } from '@indipia/ui-core';

export interface TermsConsentValue {
  terms: boolean;
  marketing: boolean;
  /** 'email' | 'sms' | 'kakao' — marketing=false면 무시됨 */
  channels: string[];
  /** 야간(21시~08시) 광고성 정보 수신 동의 — marketing=false면 무시됨 */
  nightAd: boolean;
}

export const INITIAL_TERMS_CONSENT: TermsConsentValue = {
  terms: false,
  marketing: false,
  channels: [],
  nightAd: false,
};

// 웹 가입 화면은 앱 푸시를 받을 네이티브 앱이 없으므로 이메일·SMS·알림톡만 제공한다(앱 푸시는 InsolMobile 전용).
const CHANNELS: { value: string; label: string }[] = [
  { value: 'email', label: '이메일' },
  { value: 'sms', label: 'SMS' },
  { value: 'kakao', label: '알림톡' },
];

interface TermsConsentSectionProps {
  value: TermsConsentValue;
  onChange: (v: TermsConsentValue) => void;
}

/** 동의 행 — 행 전체가 클릭 대상인 label. `.chk` 는 inline-flex 라 폭을 채우도록 flex 로 덮는다 */
const consentRow: React.CSSProperties = { display: 'flex', flex: 1, gap: 'var(--spacing-04)', alignItems: 'center' };

export function TermsConsentSection({ value, onChange }: TermsConsentSectionProps) {
  const [termsOpen, setTermsOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);

  const allChecked = value.terms && value.marketing;

  const toggleAll = () => {
    const v = !allChecked;
    onChange({
      terms: v,
      marketing: v,
      channels: v ? CHANNELS.map((c) => c.value) : [],
      nightAd: false, // 야간동의는 전체동의에 포함하지 않음 — 별도 성격의 옵션이라 사용자가 명시적으로 켜야 함(기획서 스크립트와 동일)
    });
  };

  const toggleTerms = () => onChange({ ...value, terms: !value.terms });

  const toggleMarketing = () => {
    const next = !value.marketing;
    onChange({
      ...value,
      marketing: next,
      channels: next ? CHANNELS.map((c) => c.value) : [],
      nightAd: next ? value.nightAd : false,
    });
  };

  const toggleChannel = (ch: string) => {
    const has = value.channels.includes(ch);
    const channels = has ? value.channels.filter((c) => c !== ch) : [...value.channels, ch];
    // 채널을 켜면 마케팅 동의도 자동 ON, 마지막 채널까지 끄면 자동 OFF(야간동의도 함께 해제)
    const marketing = channels.length > 0;
    onChange({ ...value, channels, marketing, nightAd: marketing ? value.nightAd : false });
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <span className="text-caption font-semibold text-text" style={{ marginBottom: 6, display: 'block' }}>약관 동의</span>
      <div style={{ border: '1px solid var(--shell-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {/* 전체 동의 — 일부만 선택된 상태는 indeterminate("─")로 표시 */}
        <label
          className="chk"
          style={{ display: 'flex', gap: 'var(--spacing-04)', padding: 'var(--spacing-04) var(--spacing-05)', borderBottom: '2px solid var(--shell-border)', background: 'var(--shell-bg)' }}
        >
          <Checkbox
            bare
            checked={allChecked}
            indeterminate={value.terms !== value.marketing}
            onChange={toggleAll}
          />
          <span className="text-body-sm font-bold text-text" style={{ flex: 1 }}>전체 동의</span>
        </label>

        {/* 필수: 이용약관 (개인정보 수집·이용 안내 포함) */}
        <div style={{ borderBottom: '1px solid var(--shell-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-04)', padding: 'var(--spacing-04) var(--spacing-05)' }}>
            <label className="chk" style={consentRow}>
              <Checkbox bare checked={value.terms} onChange={toggleTerms} />
              <span className="text-body-sm text-text">이용약관 동의 (개인정보 수집·이용 안내 포함)</span>
            </label>
            <span className="text-caption font-bold" style={{ padding: 'var(--spacing-01) var(--spacing-03)', borderRadius: 'var(--radius-full)', flexShrink: 0, background: 'var(--shell-point-subtle)', color: 'var(--shell-point)' }}>필수</span>
            <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setTermsOpen((o) => !o)}>
              {termsOpen ? '접기' : '보기'} ›
            </Button>
          </div>
          {termsOpen && (
            <div className="text-caption text-text-secondary" style={{ padding: '0 16px 16px' }}>
              서비스 제공(계약 이행)에 필요한 개인정보(상호·대표자명·사업자등록번호·가입자 성명·아이디·비밀번호·휴대전화번호·이메일)를 수집·이용합니다.
              필수 정보를 제공하지 않으면 회원가입 및 서비스 이용이 제한됩니다. 자세한 내용은{' '}
              <a href="/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--gt-primary)' }}>이용약관</a>과{' '}
              <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--gt-primary)' }}>개인정보처리방침</a>에서 확인하실 수 있습니다.
            </div>
          )}
        </div>

        {/* 선택: 마케팅 정보 수신 */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-04)', padding: 'var(--spacing-04) var(--spacing-05)' }}>
            <label className="chk" style={consentRow}>
              <Checkbox bare checked={value.marketing} onChange={toggleMarketing} />
              <span className="text-body-sm text-text">마케팅 정보 수신 동의 (이메일·SMS 등)</span>
            </label>
            <span className="text-caption font-bold text-text-secondary" style={{ padding: 'var(--spacing-01) var(--spacing-03)', borderRadius: 'var(--radius-full)', flexShrink: 0, background: 'var(--shell-border)' }}>선택</span>
            <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setMarketingOpen((o) => !o)}>
              {marketingOpen ? '접기' : '보기'} ›
            </Button>
          </div>
          {marketingOpen && (
            <div style={{ padding: '0 16px 16px' }}>
              <div style={{ padding: 'var(--spacing-05)', background: 'var(--shell-bg)', borderRadius: 'var(--radius-md)' }}>
                <p className="text-caption font-bold text-text" style={{ marginBottom: 8 }}>수신 수단 (선택)</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--gap-sm) var(--gap-md)', marginBottom: 'var(--spacing-04)' }}>
                  {CHANNELS.map((c) => (
                    <Checkbox
                      key={c.value}
                      label={c.label}
                      checked={value.channels.includes(c.value)}
                      onChange={() => toggleChannel(c.value)}
                    />
                  ))}
                </div>
                <p className="text-caption font-bold text-text" style={{ marginBottom: 8 }}>야간(오후 9시~오전 8시) 광고성 정보 수신 (별도 동의)</p>
                <div style={{ display: 'flex', gap: 'var(--gap-md)' }}>
                  <Radio
                    name="nightAd"
                    label="동의"
                    checked={value.nightAd}
                    onChange={() => onChange({ ...value, nightAd: true })}
                  />
                  <Radio
                    name="nightAd"
                    label="미동의"
                    checked={!value.nightAd}
                    onChange={() => onChange({ ...value, nightAd: false })}
                  />
                </div>
              </div>
              <p className="text-caption text-text-secondary" style={{ marginTop: 10 }}>
                마케팅 정보 수신 동의는 선택 사항이며, 동의하지 않아도 서비스 이용에 제한이 없습니다. 언제든지 가입 후 설정에서 수신을 거부·철회할 수 있습니다.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
